import React, { useMemo, useRef, useState } from "react";
import { FiAlertTriangle, FiDollarSign, FiInfo, FiLayers, FiTarget, FiTruck } from "react-icons/fi";
import { Sparkline } from "../../activity-logs/components/primitives";
import { Section, Skeleton, Widget, WidgetGrid } from "../../activity-logs/styles";
import { IOS } from "../../activity-logs/labels";
import { CompositionWidget, Notice, StepNumber, StorageBar } from "../styles";
import { CHART_COLORS, buildSupplierStats } from "../supplierStats";
import { displayName, formatCompact, formatCount, formatCurrency, formatPercent } from "../dashboardData";
import { BandChart, SupplierBarList } from "./supplierCharts";
import { MoneyValue, SupplierAvatar } from "./parts";
import { ListRow } from "./TopLists";
import SupplierList from "./SupplierList";

/**
 * Dashboard da aba Fornecedores: de quem depende o crédito e por onde vale começar.
 * Os valores vêm de /creditos/agrupado, que não aplica o filtro de período. Não há
 * corte por UF porque a API não preenche esse campo no nível do fornecedor.
 */
export default function SupplierDashboard({ suppliers, loading, failed, periodFiltered, onOpen }) {
  const stats = useMemo(() => buildSupplierStats(suppliers), [suppliers]);
  const [abc, setAbc] = useState("");
  const directory = useRef(null);

  const selectClass = (key) => {
    setAbc(key);
    directory.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const notices = (
    <>
      {periodFiltered && (
        <Notice style={{ marginTop: 0, marginBottom: 12 }}>
          <FiInfo />
          <span>Os valores por fornecedor são de todo o projeto: a API não aplica o filtro de período neste ranking.</span>
        </Notice>
      )}
      {failed && (
        <Notice style={{ marginTop: 0, marginBottom: 12 }}>
          <FiAlertTriangle />
          <span>Não foi possível consultar todos os tipos de crédito: os números abaixo podem estar incompletos.</span>
        </Notice>
      )}
    </>
  );

  if (loading && suppliers.length === 0) {
    return (
      <Section $first>
        <WidgetGrid>{[0, 1, 2, 3].map((i) => <Widget key={i}><Skeleton $h={70} $r={10} /></Widget>)}</WidgetGrid>
        <div style={{ height: 14 }} />
        <Skeleton $h={280} $r={16} />
      </Section>
    );
  }

  if (suppliers.length === 0) {
    return <>{notices}<SupplierList suppliers={[]} classes={stats.classes} abc="" setAbc={setAbc} onOpen={onOpen} /></>;
  }

  const { classes, bestReturn } = stats;

  return (
    <>
      <Section $first>
        {notices}
        <WidgetGrid data-tour="sup-kpis">
          <Widget $delay={0}>
            <div className="w-head"><FiTruck style={{ color: IOS.blue, opacity: 1 }} /> Fornecedores</div>
            <div className="w-value">{formatCount(stats.supplierCount)}</div>
            <div className="w-foot" style={{ justifyContent: "space-between" }}>
              <span>
                {stats.groups > 0 && stats.groups < stats.supplierCount
                  ? `${formatCount(stats.groups)} empresas distintas`
                  : "com crédito identificado"}
              </span>
              {/* Quantos fornecedores há em cada faixa de crédito, da menor à maior */}
              <Sparkline data={stats.bands.map((b) => b.count)} color={IOS.blue} width={80} height={24} points={60} />
            </div>
          </Widget>
          <Widget $delay={50} $clickable onClick={() => selectClass("A")}>
            <div className="w-head"><FiTarget style={{ color: IOS.mint, opacity: 1 }} /> Concentram 80% do crédito</div>
            <div className="w-value" style={{ color: IOS.mint }}>{formatCount(stats.forEighty)}</div>
            <div className="w-foot" style={{ justifyContent: "space-between" }}>
              <span>{formatPercent(stats.eightyShare)} dos fornecedores</span>
              {/* Crédito acumulado do maior para o menor: sobe rápido e achata */}
              <Sparkline data={stats.concentrationCurve} color={IOS.mint} width={80} height={24} points={60} />
            </div>
          </Widget>
          <Widget $delay={100}>
            <div className="w-head"><FiDollarSign style={{ color: IOS.indigo, opacity: 1 }} /> Crédito mediano</div>
            <div className="w-value"><MoneyValue value={stats.medianCredit} /></div>
            <div className="w-foot" style={{ justifyContent: "space-between" }}>
              <span>média {formatCompact(stats.averageCredit)}</span>
              {/* Parte do crédito em cada faixa: mostra por que a média fica longe da mediana */}
              <Sparkline data={stats.bands.map((b) => b.share)} color={IOS.indigo} width={80} height={24} points={60} />
            </div>
          </Widget>
          <Widget $delay={150}>
            <div className="w-head"><FiLayers style={{ color: IOS.purple, opacity: 1 }} /> Em mais de um imposto</div>
            <div className="w-value">{formatCount(stats.multiTax)}</div>
            <div className="w-foot" style={{ justifyContent: "space-between" }}>
              <span>{formatPercent((stats.multiTax / stats.supplierCount) * 100)} dos fornecedores</span>
              {/* Fornecedores com 1, 2, 3 e 4 impostos */}
              <Sparkline data={stats.taxSpread} color={IOS.purple} width={80} height={24} points={60} />
            </div>
          </Widget>
        </WidgetGrid>
      </Section>

      <Section $delay={80}>
        <WidgetGrid>
          <Widget $span={3} $delay={150}>
            <div className="w-title">Maiores fornecedores</div>
            <div className="w-sub" style={{ marginBottom: 12 }}>
              Os {formatCount(stats.topBars.length)} primeiros somam {formatPercent(stats.topBarsShare)} do crédito
            </div>
            <SupplierBarList suppliers={stats.topBars} colors={CHART_COLORS} onOpen={onOpen} />
          </Widget>

          <CompositionWidget data-tour="abc" $delay={200}>
            <div className="w-title">Curva ABC</div>
            <div className="w-sub">Clique para filtrar o diretório</div>
            <StorageBar role="img" aria-label="Participação de cada classe no crédito" style={{ marginTop: 14 }}>
              {classes.filter((c) => c.total > 0).map((c) => (
                <span key={c.key} title={`${c.label}: ${formatPercent(c.share)}`} style={{ flexGrow: Math.max(c.share, 0.5), background: c.color }} />
              ))}
            </StorageBar>
            <div style={{ marginTop: 8 }}>
              {classes.map((c) => (
                <ListRow
                  key={c.key}
                  lead={<StepNumber $color={c.color}>{c.key}</StepNumber>}
                  title={`${formatCount(c.count)} ${c.count === 1 ? "fornecedor" : "fornecedores"}`}
                  subtitle={c.hint}
                  value={c.total}
                  share={c.share}
                  compact
                  onClick={() => selectClass(c.key)}
                />
              ))}
            </div>
          </CompositionWidget>
        </WidgetGrid>
      </Section>

      <Section $delay={140}>
        <WidgetGrid>
          <Widget $span={2} $delay={250}>
            <div className="w-title">Fornecedores por faixa de crédito</div>
            <div className="w-sub" style={{ marginBottom: 2 }}>
              {formatCount(stats.smallCount)} têm menos de R$ 1 mil e somam {formatPercent(stats.smallShare)} do crédito. Abaixo de cada barra, a parte do crédito.
            </div>
            <BandChart bands={stats.bands} colors={CHART_COLORS} />
          </Widget>

          <Widget $span={2} $delay={300}>
            <div className="w-title">Maior crédito por item</div>
            <div className="w-sub" style={{ marginBottom: 8 }}>Classes A e B: menos itens a revisar por real identificado</div>
            {bestReturn.map((s) => (
              <ListRow
                key={s.id}
                lead={<SupplierAvatar name={s.name} size={32} />}
                title={displayName(s.name)}
                fullTitle={s.name}
                subtitle={`${formatCurrency(s.perItem)} por item · ${formatCount(s.count)} ${s.count === 1 ? "item" : "itens"}`}
                value={s.total}
                share={s.share}
                onClick={() => onOpen(s)}
              />
            ))}
          </Widget>
        </WidgetGrid>
      </Section>

      <div ref={directory} data-tour="sup-directory" style={{ scrollMarginTop: 180 }}>
        <SupplierList suppliers={stats.suppliers} classes={classes} abc={abc} setAbc={setAbc} onOpen={onOpen} />
      </div>
    </>
  );
}
